import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { IBenchConsultant } from "../../models/BenchConsultant";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { JVInput } from "../../shared/JVInput";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVDate } from "../../shared/JVDate";
import { IUsersDropdown } from "../../models/IDropdown";
import { JVMultiselect } from "../../shared/JVMultiselect";
import { IH1B_Assign } from "./SalesManagerList";

interface IMarketing_Status {
    status_ID?: number,
    consultant_ID?: number,
    marketing_Status?: string,
    marketing_Priority?: string,
    mStart_Date?: Date,
    hold_Reason?: string,
    holdby?: number,
    hold_Date?: Date,
    confirmation_Date?: Date,
    confirmedBy?: number,
    joining_Date?: Date,
}

const schema = yup.object({
    marketing_Status: yup.string().required("Marketing Status is required"),

    marketing_Priority: yup.string().when('marketing_Status', val => {
        return (val === "Marketing Start") ?
            yup.string().required("Priority is required") :
            yup.string().nullable()
    }),
    mStart_Date: yup.date().when('marketing_Status', val => {
        return (val === "Marketing Start") ?
            yup.date().required("Marketing Start Date is required").typeError("Marketing Start Date is required") :
            yup.string().nullable()
    }),

    hold_Reason: yup.string().when('marketing_Status', val => {
        return (val === "Hold") ?
            yup.string().required("Reason is required") :
            yup.string().nullable()
    }),
    holdby: yup.number().when('marketing_Status', val => {
        return (val === "Hold") ?
            yup.number().min(1, "Hold By is required").required("Hold By is required") :
            yup.number().nullable()
    }),
    hold_Date: yup.date().when('marketing_Status', val => {
        return (val === "Hold") ?
            yup.date().required("Hold Date is required").typeError("Hold Date is required") :
            yup.string().nullable()
    }),

    confirmation_Date: yup.date().when('marketing_Status', val => {
        return (val === "Stop") ?
            yup.date().required("Confirmation Date is required").typeError("Confirmation Date is required") :
            yup.string().nullable()
    }),
    confirmedBy: yup.number().when('marketing_Status', val => {
        return (val === "Stop") ?
            yup.number().min(1, "Confirmed By is required").required("Confirmed By is required") :
            yup.number().nullable()
    }),
    joining_Date: yup.date().when('marketing_Status', val => {
        return (val === "Stop") ?
            yup.date().required("Joining Date is required").typeError("Joining Date is required") :
            yup.string().nullable()
    })
});

interface Iprops {
    singleData: IBenchConsultant,
    open: boolean,
    hideDialog: () => void,
    toast: any,
    reload: boolean,
    setReload: (is: boolean) => void
}

export const Status = (props: Iprops) => {
    const [loading, setLoading] = useState(false);
    const [mstatus, setMStatus] = useState(0);
    const [users, setUsers] = useState<Array<IUsersDropdown>>();
    const [selectedSales, setSelectedSales] = useState<Array<IH1B_Assign>>();
    const [sales, setSales] = useState<Array<IH1B_Assign>>([]);

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<IMarketing_Status>({
        resolver: yupResolver(schema),
        defaultValues: { marketing_Status: "---Select---" },
        shouldFocusError: false
    });

    const loadUsers = async () => {
        const response = await Fetch("Dropdown/users", "GET", null, null, null);
        if (response) {
            const data: Array<IUsersDropdown> = await response.json();
            setUsers(data);
        }
    }

    const loadSales = async () => {
        const response = await Fetch("Consultant_Bench/allocate", "GET", null, `consultant_ID=${props.singleData.consultant_ID}`, null);
        if (response) {
            const data: Array<IH1B_Assign> = await response.json();
            setSales(data);
            const _data: Array<IH1B_Assign> = [];
            data.forEach(d => { if (d.assign_ID) _data.push(d); });
            setSelectedSales(_data);
        }
    }

    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === "marketing_Status") {
                if (value.marketing_Status === "Marketing Start") setMStatus(1);
                else if (value.marketing_Status === "Hold") setMStatus(2);
                else if (value.marketing_Status === "Stop") setMStatus(3);
                else setMStatus(0);
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    useEffect(() => {
        if (props.singleData.consultant_ID) loadSales();
    }, [props.singleData.consultant_ID]);

    useEffect(() => {
        loadUsers();
    }, []);

    const saveToDB = async (body: IMarketing_Status) => {
        setLoading(true);

        const h1B_Assign: Array<IH1B_Assign> = [];
        if (body.marketing_Status === "Marketing Start") {
            selectedSales?.forEach(s => {
                s.assign_ID = undefined;
                s.consultant_ID = props.singleData.consultant_ID;
                s.sales_ID = s.login_ID;
                h1B_Assign.push(s);
            });
            body.hold_Date = undefined;
            body.confirmation_Date = undefined;
            body.joining_Date = undefined;
        } else if (body.marketing_Status === "Hold") {
            body.mStart_Date = undefined;
            body.confirmation_Date = undefined;
            body.joining_Date = undefined;
        } else if (body.marketing_Status === "Stop") {
            body.mStart_Date = undefined;
            body.hold_Date = undefined;
        }

        body.consultant_ID = props.singleData.consultant_ID;
        const response = await Fetch("Consultant_Bench/status", "PUT", body, `h1B_Assign=${JSON.stringify(h1B_Assign)}`, props.toast);

        if (response) {
            onHide();
            props.toast.current.show({ severity: 'success', detail: "Updated Successfully.", life: 3000 });
            props.setReload(!props.reload);
        }
        setLoading(false);
    }

    const onHide = () => {
        props.hideDialog();
        reset();
        setMStatus(0);
        setSelectedSales(undefined);
    }

    function OnSubmit(data: IBenchConsultant) {
        if ((data.marketing_Status === "Marketing Start" && selectedSales?.length && selectedSales.length > 0) || data.marketing_Status !== "Marketing Start")
            saveToDB(data);
        else props.toast.current.show({ severity: 'error', detail: "Select Sales for Allocaton.", life: 3000 });
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Change Marketing Status" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(OnSubmit)} className="p-fluid formgrid grid">
                <JVDropdown required column={2} label="Marketing Status" control={control} name="marketing_Status" options={[{ status: "---Select---" }, { status: "Marketing Start" }, { status: "Hold" }, { status: "Stop" }]} optionLabel="status" optionValue="status" placeholder="Select Status" error={errors.marketing_Status?.message} />
                <div className="field col-12 md:col-6"></div>

                {mstatus === 1 && <>
                    <JVDropdown required column={3} label="Priority" control={control} name="marketing_Priority" options={[{ priority: "High" }, { priority: "Medium" }, { priority: "Low" }]} optionLabel="priority" optionValue="priority" placeholder="Select Priority" error={errors.marketing_Priority?.message} />
                    <JVMultiselect column={3} label="Alloted To" options={sales} optionLabel="user_Name" placeholder="Select Sales" required value={selectedSales} onChange={setSelectedSales} />
                    <JVDate required column={3} label="Start Date" control={control} name="mStart_Date" error={errors.mStart_Date?.message} />
                </>} {mstatus === 2 && <>
                    <JVInput column={3} required label="Reason" control={control} name="hold_Reason" error={errors.hold_Reason?.message} />
                    <JVDropdown column={3} required label="Hold By" control={control} name="holdby" options={users} optionLabel="user_Name" optionValue="login_ID" placeholder="Select User" error={errors.holdby?.message} />
                    <JVDate required column={3} label="Hold Date" control={control} name="hold_Date" error={errors.hold_Date?.message} />
                </>} {mstatus === 3 && <>
                    <JVDate required column={3} label="Confirmation Date" control={control} name="confirmation_Date" error={errors.confirmation_Date?.message} />
                    <JVDropdown column={3} required label="Confirmation By" control={control} name="confirmedBy" options={users} optionLabel="user_Name" optionValue="login_ID" placeholder="Select User" error={errors.confirmedBy?.message} />
                    <JVDate required column={3} label="Joining Date" control={control} name="joining_Date" error={errors.joining_Date?.message} />
                </>}
                {mstatus !== 0 && <div className="field col-12 md:col-6">
                    <label htmlFor="">&nbsp;</label>
                    <Button loading={loading} label="SAVE" type="submit"></Button>
                </div>}
            </form>
        </Dialog>
    );
}
